/**
 * TenantEditModal Component
 * Modal for editing tenant details
 */

import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { Modal, Button, Input } from '../../../components/common';
import { useUpdateTenant } from '../../../hooks/useSuperAdmin';
import type { TenantListItem, UpdateTenantRequest } from '../../../types/superadmin';

interface TenantEditModalProps {
  tenant: TenantListItem;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const TenantEditModal: React.FC<TenantEditModalProps> = ({
  tenant,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: tenant.name,
    logo: tenant.logo || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateTenantMutation = useUpdateTenant();

  // Reset form when tenant changes
  useEffect(() => {
    setFormData({
      name: tenant.name,
      logo: tenant.logo || '',
    });
    setErrors({});
  }, [tenant]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters';
    }

    if (formData.logo && !/^https?:\/\/.+/.test(formData.logo)) {
      newErrors.logo = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const request: UpdateTenantRequest = {
      name: formData.name.trim(),
      logo: formData.logo.trim() || null,
    };

    try {
      await updateTenantMutation.mutateAsync({
        tenantId: tenant.id,
        data: request,
      });
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Tenant">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center gap-3 p-4 bg-stone-100 rounded-lg">
          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            {tenant.logo ? (
              <img
                src={tenant.logo}
                alt={tenant.name}
                className="w-10 h-10 rounded"
              />
            ) : (
              <Building2 className="w-6 h-6 text-indigo-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-stone-900">{tenant.name}</p>
            <p className="text-sm text-stone-500">
              {tenant.userCount} users, {tenant.okrCount} OKRs
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Organization Name *
            </label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Organization name"
              error={errors.name}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Logo URL
            </label>
            <Input
              name="logo"
              value={formData.logo}
              onChange={handleChange}
              placeholder="https://example.com/logo.png"
              error={errors.logo}
            />
            <p className="mt-1 text-xs text-stone-500">
              Enter a URL to the organization's logo image
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={updateTenantMutation.isPending}
          >
            {updateTenantMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
